import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * Hidden /brand page — visual QA for every generated app icon + splash size
 * before submitting to Apple App Store, Google Play Store, or updating the
 * PWA. Not linked from the main nav (intentional). Access via
 * `https://…/brand`.
 */

const PWA_ICONS = [48, 72, 96, 128, 192, 256, 512];

// Every Android density mipmap that @capacitor/assets produced.
// Path is relative to the running dev server — we cannot render these directly
// because they live under /app/frontend/android/... (outside /public). We show
// the path + size so the QA reviewer can open them in Finder / File Explorer.
const ANDROID_DENSITIES = [
  { name: "mdpi (baseline)",   px: "48×48",     path: "android/app/src/main/res/mipmap-mdpi/ic_launcher.png" },
  { name: "hdpi",              px: "72×72",     path: "android/app/src/main/res/mipmap-hdpi/ic_launcher.png" },
  { name: "xhdpi",             px: "96×96",     path: "android/app/src/main/res/mipmap-xhdpi/ic_launcher.png" },
  { name: "xxhdpi",            px: "144×144",   path: "android/app/src/main/res/mipmap-xxhdpi/ic_launcher.png" },
  { name: "xxxhdpi",           px: "192×192",   path: "android/app/src/main/res/mipmap-xxxhdpi/ic_launcher.png" },
  { name: "adaptive fg (xxxhdpi)", px: "432×432", path: "android/app/src/main/res/mipmap-xxxhdpi/ic_launcher_foreground.png" },
  { name: "round (xxxhdpi)",   px: "192×192",   path: "android/app/src/main/res/mipmap-xxxhdpi/ic_launcher_round.png" },
];

const IOS_ICON_SIZES = [
  { name: "1024×1024 (AppIcon @2x)", path: "ios/App/App/Assets.xcassets/AppIcon.appiconset/AppIcon-512@2x.png" },
];

const IOS_SPLASHES = [
  { name: "Universal @1x", path: "ios/App/App/Assets.xcassets/Splash.imageset/Default@1x~universal~anyany.png" },
  { name: "Universal @2x", path: "ios/App/App/Assets.xcassets/Splash.imageset/Default@2x~universal~anyany.png" },
  { name: "Universal @3x", path: "ios/App/App/Assets.xcassets/Splash.imageset/Default@3x~universal~anyany.png" },
  { name: "Universal @1x — dark", path: "ios/App/App/Assets.xcassets/Splash.imageset/Default@1x~universal~anyany-dark.png" },
  { name: "Universal @2x — dark", path: "ios/App/App/Assets.xcassets/Splash.imageset/Default@2x~universal~anyany-dark.png" },
];

function Row({ children, title, subtitle }) {
  return (
    <section className="border-t border-zinc-200 dark:border-zinc-800 py-10">
      <div className="overline mb-1">{title}</div>
      {subtitle && <div className="text-sm text-zinc-500 mb-6">{subtitle}</div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
        {children}
      </div>
    </section>
  );
}

function IconTile({ src, label, sub, checkerboard = false }) {
  return (
    <div
      className="border border-zinc-200 dark:border-zinc-800 p-5 flex flex-col items-center gap-3"
      data-testid={`icon-tile-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div
        className={`w-full aspect-square flex items-center justify-center ${
          checkerboard
            ? "bg-[conic-gradient(#f4f4f5_0_25%,white_0_50%,#f4f4f5_0_75%,white_0)] bg-[length:16px_16px] dark:bg-[conic-gradient(#27272a_0_25%,#18181b_0_50%,#27272a_0_75%,#18181b_0)]"
            : "bg-zinc-50 dark:bg-zinc-900"
        }`}
      >
        {src && <img src={src} alt={label} className="max-w-full max-h-full" />}
      </div>
      <div className="w-full">
        <div className="font-mono text-xs text-zinc-950 dark:text-zinc-100">{label}</div>
        {sub && <div className="font-mono text-[10px] text-zinc-500 truncate">{sub}</div>}
      </div>
    </div>
  );
}

function PathTile({ path, label, subtitle }) {
  return (
    <div
      className="border border-zinc-200 dark:border-zinc-800 p-5 flex flex-col gap-2"
      data-testid={`asset-path-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="font-mono text-xs text-zinc-950 dark:text-zinc-100">{label}</div>
      {subtitle && <div className="font-mono text-[10px] text-zinc-500">{subtitle}</div>}
      <code className="font-mono text-[10px] text-zinc-500 break-all">{path}</code>
    </div>
  );
}

export default function BrandPage() {
  const [manifest, setManifest] = useState(null);

  useEffect(() => {
    fetch("/manifest.webmanifest")
      .then((r) => (r.ok ? r.json() : null))
      .then(setManifest)
      .catch(() => setManifest(null));
    // update page title so it's obvious in browser tabs
    document.title = "Symposia — Brand & Icons QA";
  }, []);

  return (
    <div className="px-6 md:px-10 lg:px-16 py-12 md:py-16" data-testid="brand-page">
      <div className="overline mb-2">Internal — Not Indexed</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-[-0.03em] leading-[.95] mb-4">
        Brand &amp; Icons QA
      </h1>
      <p className="max-w-2xl text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">
        A visual dashboard of every generated icon &amp; splash asset. Use this
        page as the final gate before submitting a new build to Apple App Store,
        Google Play, or updating the PWA. The rest of the app doesn&rsquo;t link
        here — open <code className="font-mono">/brand</code> directly.
      </p>

      {/* Source */}
      <Row title="Source" subtitle="1024×1024 master PNG used to generate all other sizes.">
        <IconTile src="/icons/icon-512.webp" label="Master mark" sub="resources/icon.png (rendered @512 here)" />
      </Row>

      {/* PWA */}
      <Row title="PWA Web Icons" subtitle="Rendered from /public/icons/*.webp — used by the browser and installed home-screen shortcuts.">
        {PWA_ICONS.map((s) => (
          <IconTile
            key={s}
            src={`/icons/icon-${s}.webp`}
            label={`${s}×${s}`}
            sub={`/icons/icon-${s}.webp`}
            checkerboard={false}
          />
        ))}
      </Row>

      {/* PWA icon maskable preview */}
      <Row title="Maskable Safe Zone" subtitle="Every icon is declared purpose='any maskable'. This circle shows the safe area Android uses when applying an adaptive shape mask.">
        {[192, 512].map((s) => (
          <div key={s} className="border border-zinc-200 dark:border-zinc-800 p-5 flex flex-col items-center gap-3" data-testid={`maskable-${s}`}>
            <div className="w-full aspect-square relative">
              <img src={`/icons/icon-${s}.webp`} alt={`icon-${s}`} className="absolute inset-0 w-full h-full" />
              <div className="absolute inset-[10%] rounded-full ring-2 ring-emerald-500/70 pointer-events-none" />
            </div>
            <div className="font-mono text-xs text-zinc-950 dark:text-zinc-100">Safe zone · {s}×{s}</div>
          </div>
        ))}
      </Row>

      {/* Manifest sanity */}
      <Row title="Web App Manifest" subtitle="Values that browsers + PWA installers read.">
        <div className="border border-zinc-200 dark:border-zinc-800 p-5 col-span-2 md:col-span-3 lg:col-span-4" data-testid="manifest-preview">
          <pre className="font-mono text-xs whitespace-pre-wrap break-all text-zinc-800 dark:text-zinc-300">
{manifest ? JSON.stringify(manifest, null, 2) : "loading manifest…"}
          </pre>
        </div>
      </Row>

      {/* iOS */}
      <Row title="iOS Native" subtitle="Files inside the Xcode project. Open them in Finder — they can't be served over http from the SPA.">
        {IOS_ICON_SIZES.map((it) => (
          <PathTile key={it.path} label={it.name} path={it.path} subtitle="Xcode: AppIcon.appiconset" />
        ))}
        {IOS_SPLASHES.map((it) => (
          <PathTile key={it.path} label={it.name} path={it.path} subtitle="Xcode: Splash.imageset" />
        ))}
      </Row>

      {/* Android */}
      <Row title="Android Native" subtitle="Files inside the Gradle project. Open them in File Explorer.">
        {ANDROID_DENSITIES.map((it) => (
          <PathTile key={it.path} label={it.name} path={it.path} subtitle={it.px} />
        ))}
        <PathTile
          label="splash (portrait xxxhdpi)"
          path="android/app/src/main/res/drawable-port-xxxhdpi/splash.png"
          subtitle="2732×2732"
        />
        <PathTile
          label="splash-dark (portrait xxxhdpi)"
          path="android/app/src/main/res/drawable-port-night-xxxhdpi/splash.png"
          subtitle="2732×2732"
        />
      </Row>

      {/* Regeneration */}
      <section className="mt-14 border-t border-zinc-200 dark:border-zinc-800 pt-10">
        <div className="overline mb-3">Regeneration</div>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 max-w-2xl">
          To regenerate all sizes (after tweaking <code className="font-mono">resources/icon.png</code> or{" "}
          <code className="font-mono">resources/splash.png</code>):
        </p>
        <pre className="mt-4 p-4 bg-zinc-950 text-zinc-100 dark:bg-zinc-900 font-mono text-xs overflow-x-auto">
{`cd /app/frontend
npx capacitor-assets generate \\
  --iconBackgroundColor '#09090B' \\
  --iconBackgroundColorDark '#09090B' \\
  --splashBackgroundColor '#09090B' \\
  --splashBackgroundColorDark '#09090B'`}
        </pre>
      </section>

      <div className="mt-14 text-sm">
        <Link to="/" className="link-ul">← Back to Symposia</Link>
      </div>
    </div>
  );
}
